import { useEffect, useRef } from 'react';

/**
 * Trap keyboard focus inside a modal dialog panel, and restore it on close.
 *
 * The detail drawers (People's PersonDetail, Teams' TeamDetail) are
 * `aria-modal` dialogs, but marking a panel `aria-modal` does nothing on its
 * own — a keyboard or screen-reader user pressing Tab still walks straight out
 * of the panel and into the roster behind it, which is a WCAG 2.2 AA failure
 * and a procurement gate for us. This hook makes the modality real:
 *
 * - focus moves into the panel on open (the first focusable — the close
 *   button — matching what the drawers did by hand),
 * - Tab / Shift-Tab cycle within the panel's focusable elements and never
 *   escape it,
 * - Escape closes (so the one keydown listener owns both behaviours), and
 * - focus returns to the control that opened the drawer on close, so the
 *   keyboard user lands back where they were rather than at the top of the page.
 *
 * Shared by both drawers rather than reimplemented in each — one dialog focus
 * contract, one place to get it right.
 *
 * @param onClose called on Escape; also the trigger whose focus is restored.
 * @returns a ref to attach to the dialog panel element.
 */
export function useDialogFocusTrap<T extends HTMLElement>(onClose: () => void) {
  const ref = useRef<T>(null);
  // Keep the latest onClose without re-running the effect (which would re-focus
  // the panel on every render).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const panel = ref.current;
    if (!panel) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;

    const focusables = (): HTMLElement[] =>
      Array.from(
        panel.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );

    // Move focus into the panel: the first focusable (the close button in both
    // drawers), or the panel itself if it somehow holds none.
    const first = focusables()[0];
    if (first) {
      first.focus();
    } else {
      panel.tabIndex = -1;
      panel.focus();
    }

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;

      const items = focusables();
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const firstEl = items[0]!;
      const lastEl = items[items.length - 1]!;
      const active = document.activeElement;

      // Wrap at both ends, and pull focus back in if it has somehow left the
      // panel (e.g. after a click on the backdrop).
      if (e.shiftKey) {
        if (active === firstEl || !panel.contains(active)) {
          e.preventDefault();
          lastEl.focus();
        }
      } else if (active === lastEl || !panel.contains(active)) {
        e.preventDefault();
        firstEl.focus();
      }
    };

    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      // Restore focus to the invoking control so the keyboard user is not
      // dumped at the top of the document.
      previouslyFocused?.focus?.();
    };
    // Run once per mount: the drawer is remounted per open (keyed on the id),
    // so this establishes and tears down the trap for one dialog lifetime.
    // onClose is read through onCloseRef, so it is deliberately not a dep.
  }, []);

  return ref;
}
